import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import useApiGet from '../hooks/getData';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useSelector } from 'react-redux';
import NoData from '../../assets/NoData.png';

function TopStoriesList() {
  const token = Cookies.get("Snapta_Admin_Token");
  const { getData } = useApiGet();
  const isSidebarOpen = useSelector((state) => state.sidebar.isOpen);

  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);

  const fetchStories = async () => {
    setLoading(true);
    try {
      const res = await getData('/get_top_stories');
      setStories(res?.data?.top_stories || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStories(); }, []);

  const resetForm = () => {
    setTitle("");
    setSortOrder(0);
    setIsActive(true);
    setThumbnailFile(null);
    setVideoFile(null);
    setEditingId(null);
  };

  const openAddForm = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (story) => {
    setTitle(story.title);
    setSortOrder(story.sort_order);
    setIsActive(!!story.is_active);
    setThumbnailFile(null);
    setVideoFile(null);
    setEditingId(story.id);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!title) {
      toast.error("Please enter a title");
      return;
    }
    if (!editingId && (!thumbnailFile || !videoFile)) {
      toast.error("Please select both thumbnail and video");
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("sort_order", sortOrder);
      formData.append("is_active", isActive ? 1 : 0);
      if (thumbnailFile) formData.append("thumbnail", thumbnailFile);
      if (videoFile) formData.append("video", videoFile);
      if (editingId) formData.append("id", editingId);

      const apiUrl = process.env.REACT_APP_API_URL;
      const endpoint = editingId ? "edit_top_story" : "add_top_story";

      const res = await axios.post(`${apiUrl}/${endpoint}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success(res?.data?.message || "Saved successfully!");
      setShowForm(false);
      resetForm();
      fetchStories();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this story?")) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const res = await axios.post(`${apiUrl}/delete_top_story`, { id }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res?.data?.message || "Deleted successfully!");
      fetchStories();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className={`dark:bg-primary ${isSidebarOpen ? "xl:pl-20" : "xl:pl-72"}`}>
      <div className="px-4 pb-10 pt-6 xl:px-6">

        <div className="flex items-center justify-between pb-5">
          <h2 className="text-[#000000] font-semibold font-poppins text-xl dark:text-darkText">
            Top Stories
          </h2>
          <button
            style={{ backgroundColor: '#593A99' }}
            className="px-6 py-2.5 text-sm font-medium text-white rounded-lg font-poppins"
            onClick={openAddForm}
          >
            + Add Story
          </button>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="border border-opacity-gradient dark:border-[#1F1F1F] bg-[#FFFFFF] dark:bg-primary rounded-lg p-6 mb-6">
            <h3 className="text-base font-semibold font-poppins text-header pb-4">
              {editingId ? "Edit Story" : "Add New Story"}
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-semibold font-poppins dark:text-darkText">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="border border-opacity-gradient dark:bg-transparent dark:text-darkText dark:border-borderColor rounded-lg w-full py-3 my-1 px-4 focus:outline-none focus:ring-1 focus:ring-header"
                  placeholder="Story title"
                />
              </div>
              <div>
                <label className="text-sm font-semibold font-poppins dark:text-darkText">Sort Order</label>
                <input
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="border border-opacity-gradient dark:bg-transparent dark:text-darkText dark:border-borderColor rounded-lg w-full py-3 my-1 px-4 focus:outline-none focus:ring-1 focus:ring-header"
                />
              </div>
              <div>
                <label className="text-sm font-semibold font-poppins dark:text-darkText">
                  Thumbnail Image {editingId && "(leave empty to keep current)"}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setThumbnailFile(e.target.files[0])}
                  className="w-full py-2 my-1 text-sm font-poppins dark:text-darkText"
                />
              </div>
              <div>
                <label className="text-sm font-semibold font-poppins dark:text-darkText">
                  Video File {editingId && "(leave empty to keep current)"}
                </label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => setVideoFile(e.target.files[0])}
                  className="w-full py-2 my-1 text-sm font-poppins dark:text-darkText"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  id="is_active"
                />
                <label htmlFor="is_active" className="text-sm font-poppins dark:text-darkText">Active</label>
              </div>
            </div>

            <div className="flex gap-3 pt-5">
              <button
                onClick={handleSubmit}
                disabled={saving}
                style={{ backgroundColor: '#593A99' }}
                className="px-8 py-2.5 text-sm font-medium text-white rounded-lg font-poppins"
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={() => { setShowForm(false); resetForm(); }}
                className="px-8 py-2.5 text-sm font-medium border border-header rounded-lg font-poppins dark:text-darkText"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* List */}
        {loading ? (
          <p className="font-poppins dark:text-darkText">Loading...</p>
        ) : stories.length > 0 ? (
          <div className="overflow-hidden bg-white border border-gray-200 dark:bg-primary dark:border-[#1F1F1F] rounded-xl">
            <div className="flex items-center px-6 py-4 bg-[#00000005] dark:bg-[#1F1F1F] text-[#5B5B5B] dark:text-tableDarkLarge font-poppins text-sm font-semibold">
              <div className="w-[10%]">Thumbnail</div>
              <div className="w-[40%]">Title</div>
              <div className="w-[10%] text-center">Order</div>
              <div className="w-[15%] text-center">Status</div>
              <div className="w-[25%] text-center">Action</div>
            </div>
            {stories.map((story) => (
              <div key={story.id} className="flex items-center px-6 py-4 border-t border-gray-100 dark:border-[#1F1F1F]">
                <div className="w-[10%]">
                  <img src={story.thumbnail} alt={story.title} className="object-cover w-14 h-14 rounded-lg" />
                </div>
                <div className="w-[40%] font-poppins text-sm dark:text-darkText truncate pr-2">
                  {story.title}
                </div>
                <div className="w-[10%] text-center font-poppins text-sm dark:text-darkText">
                  {story.sort_order}
                </div>
                <div className="w-[15%] text-center">
                  <span className={`px-3 py-1 text-xs rounded-full font-poppins ${
                    story.is_active
                      ? "text-[#0D9947] bg-[#0D9947] bg-opacity-15"
                      : "text-[#999999] bg-[#999999] bg-opacity-15"
                  }`}>
                    {story.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="w-[25%] flex justify-center gap-2">
                  <button
                    onClick={() => openEditForm(story)}
                    className="px-4 py-1.5 text-xs font-poppins text-[#593A99] border border-[#593A99] rounded-lg"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(story.id)}
                    className="px-4 py-1.5 text-xs font-poppins text-[#D92D20] border border-[#D92D20] rounded-lg"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-32">
            <img src={NoData} alt="no data" className="w-96" />
            <h2 className="text-sm font-semibold font-poppins text-[#000000]">
              Don't have any data to show
            </h2>
          </div>
        )}
      </div>
    </div>
  );
}

export default TopStoriesList;